<template>
  <div
    ref="scrollableContent"
    v-prevent-body-scroll
    class="fixed left-0 right-0 top-0 z-50 h-screen transform overflow-y-scroll bg-theme-background/50 backdrop-blur"
    @scroll="onScroll"
  >
    <div
      class="mx-auto flex h-full min-h-screen w-full flex-col items-center px-4"
      style="min-height: calc(100vh + 2px)"
    >
      <div class="h-10 w-full shrink-0"></div>

      <div
        v-click-outside="onClickOutsideContent"
        class="w-[550px] max-w-full shrink-0 rounded-xl bg-theme-background shadow-lg portrait:w-[650px]"
      >
        <div
          v-if="shouldDisplayMedia"
          class="flex w-full shrink-0 items-center overflow-hidden"
        >
          <div
            class="relative z-40 w-full"
            :style="{
              paddingBottom: `${
                product.image && product.image.height && product.image.width
                  ? (product.image.height / product.image.width) * 100
                  : 100
              }%`,
            }"
          >
            <div
              style="transform: translate3d(0, 0, 0)"
              class="absolute right-7 top-7 z-20"
            >
              <ADeepCircleButton shadow @click="hide">
                <template #icon><XBold /></template>
              </ADeepCircleButton>
            </div>

            <video
              v-if="product.video && isOnline"
              class="absolute inset-0 h-full w-full rounded-t-xl bg-center object-cover"
              autoplay
              controls
              muted
              playsinline
              loop
              :poster="product.image?.url"
              width="100%"
              @play="
                setTimeout(
                  () => (($event.target as HTMLVideoElement).controls = false),
                  50,
                )
              "
              @click="($event.target as HTMLVideoElement).controls = true"
              @mouseenter="($event.target as HTMLVideoElement).controls = true"
            >
              <source :src="product.video.url" type="video/mp4" />
            </video>

            <AImage
              v-else-if="product.image"
              :src="product.image.url"
              class="absolute inset-0 h-full w-full rounded-t-xl bg-center object-cover"
              :alt="localize(product.name) ?? ''"
            />
          </div>
        </div>

        <div class="w-full px-6 py-5">
          <div class="flex justify-between">
            <div class="flex min-w-0 items-center">
              <div
                class="min-w-0 break-words font-primary text-xl font-semibold"
              >
                {{ localize(product.name) }}
              </div>
              <div class="h-6"></div>
            </div>
            <div class="flex shrink-0 dir-ltr:pl-6 dir-rtl:pr-6">
              <template v-if="restaurant.isWishlistEnabled">
                <ADeepCircleButton
                  v-if="!favoriteProductsStore.isFavorite(product.id)"
                  class="dir-ltr:ml-3 dir-rtl:mr-3"
                  @click="favoriteProductsStore.addToFavorites(product.id)"
                >
                  <template #icon><HeartBold /></template>
                </ADeepCircleButton>
                <ADeepCircleButton
                  v-else
                  class="dir-ltr:ml-3 dir-rtl:mr-3"
                  @click="favoriteProductsStore.removeFromFavorites(product.id)"
                >
                  <template #icon><HeartFill /></template>
                </ADeepCircleButton>
              </template>

              <ADeepCircleButton
                v-if="!shouldDisplayMedia"
                class="dir-ltr:ml-3 dir-rtl:mr-3"
                @click="hide"
              >
                <template #icon><XBold /></template>
              </ADeepCircleButton>
            </div>
          </div>

          <div
            v-if="product.ingredientWarnings.length > 0"
            class="mt-1 text-sm italic text-theme-text/80"
          >
            {{
              product.ingredientWarnings
                .map((w) => `${localize(w.title, { fallbackToEn: true })}`)
                .join(', ')
            }}
          </div>

          <ProductBadges class="mt-2 empty:hidden" :product="product" />

          <div
            class="mt-3 whitespace-pre-line break-words text-theme-text/90 [&_a]:underline"
          >
            <div
              class="[&>p:empty]:h-[1em] [&>p]:mb-2"
              v-html="localize(product.description)"
            />
            <div
              v-if="menu && menu.globalProductNote"
              class="[&>p:empty]:h-[1em] [&>p]:mb-2"
              v-html="localize(menu.globalProductNote)"
            />
          </div>

          <div class="mt-5">
            <div class="divide-y divide-theme-text/20">
              <ProductRadioOption
                v-for="(variant, variantIndex) in product.variants"
                :key="variantIndex"
                :name="localize(variant.name) ?? ''"
                :price="variant.price"
                :old-price="variant.oldPrice"
                :selected="selectedVariantId === variant.id"
                :readonly="
                  !getSharedData().isOrderMode || product.variants.length === 1
                "
                @click="selectedVariantId = variant.id"
              />
            </div>
          </div>

          <div>
            <div
              v-for="modifierSet in modifierSets"
              :key="modifierSet.id"
              class="mt-6"
            >
              <div class="mb-2 flex flex-wrap items-center justify-between">
                <div class="mr-4 text-lg font-semibold">
                  {{ localize(modifierSet.name) }}
                </div>

                <div class="opacity-70">
                  {{ selectionRequirementsLabelForModifierSet(modifierSet) }}
                </div>
              </div>

              <div class="divide-y divide-theme-text/20">
                <ProductRadioOption
                  v-for="modifier in modifierSet.modifiers"
                  :key="modifier.id"
                  :name="localize(modifier.name) ?? ''"
                  :price="modifier.price"
                  :old-price="null"
                  :checkbox="
                    modifierSet.minSelection !== 1 ||
                    modifierSet.maxSelection !== 1
                  "
                  price-plus
                  :selected="
                    Boolean(
                      selectedModifierIds[modifierSet.id]?.includes(
                        modifier.id,
                      ),
                    )
                  "
                  :readonly="!getSharedData().isOrderMode"
                  @click="selectModifier(modifierSet, modifier)"
                />
              </div>
            </div>
          </div>
        </div>

        <div
          v-if="getSharedData().isOrderMode && orderingConfiguration"
          class="sticky bottom-0 z-40 mt-4 flex items-center rounded-b-xl bg-theme-background px-4 pb-4 pt-4"
          style="box-shadow: 0 -1px 24px #0000001f"
        >
          <div class="flex justify-center">
            <QuantityPicker v-model="quantity" :min="1" />
          </div>
          <button
            type="button"
            class="menu-button-primary h-12 w-full !text-sm dir-ltr:ml-3 dir-rtl:mr-3 md:!text-base"
            :disabled="
              pendingAddToCartTotalPrice <= 0 &&
              !orderingConfiguration.canOrderFreeItems
            "
            @click="addToCart"
          >
            {{ trans('Add to cart') }}
            <span v-if="pendingAddToCartTotalPrice > 0"
              >({{ formatPrice(pendingAddToCartTotalPrice) }})</span
            >
          </button>
        </div>
      </div>
      <div class="flex shrink-0 justify-center py-4">
        <button
          type="button"
          class="flex h-8 items-center rounded-full border border-theme-text px-2 focus:outline-none"
          @click="hide"
        >
          <XBold class="mx-px h-4 w-4" />
          <span class="mx-px text-sm">{{ trans('Close') }}</span>
        </button>
      </div>
      <div class="h-32 w-full shrink-0"></div>
    </div>
  </div>
</template>

<script setup lang="ts">
import ADeepCircleButton from '@menu/Shared/ADeepCircleButton.vue';
import ProductBadges from '@menu/Shared/ProductBadges.vue';
import AImage from '@menu/Shared/AImage.vue';
import axios from 'axios';
import QuantityPicker from '@menu/Shared/QuantityPicker.vue';
import ProductRadioOption from '@menu/Shared/ProductRadioOption.vue';
import { isEqual } from 'lodash';
import HeartBold from '@/phosphoricons/HeartBold.vue';
import HeartFill from '@/phosphoricons/HeartFill.vue';
import XBold from '@/phosphoricons/XBold.vue';
import { useFavoriteProductsStore } from '@menu/Stores/favoriteProducts';
import { trans } from '@menu/Utils/i18n';
import { localize } from '@menu/Utils/i18n';
import { useCartStore } from '@menu/Stores/cart';
import { useIsOnline } from '@menu/Composables/useIsOnline';
import formatPrice from '@menu/Utils/formatPrice';
import getSharedData from '@menu/Utils/getSharedData';
import { computed, onMounted, reactive, ref, toValue } from 'vue';

import { useEventListener } from '@/composables/useEventListener';
import { useToast } from 'vue-toastification';
import { ModifierSetResource } from '@menu/Types/Resources/ModifierSetResource';
import { ModifierResource } from '@menu/Types/Resources/ModifierResource';
import { ProductResource } from '@menu/Types/Resources/ProductResource';
import { MenuResource } from '@menu/Types/Resources/MenuResource';
import getRestaurant from '@menu/Utils/getRestaurant';

const props = defineProps<{
  product: ProductResource;
  menu: MenuResource;
}>();

const emit = defineEmits<{
  hide: [];
}>();

const setTimeout = window.setTimeout;

const favoriteProductsStore = useFavoriteProductsStore();
const cartStore = useCartStore();
const { isOnline } = useIsOnline();
const toast = useToast();

const restaurant = getRestaurant();

const modifierSets = computed(() => {
  const modifierSets: ModifierSetResource[] = [];

  props.product.modifierSetIds.forEach((modifierSetId) => {
    const modifierSet = props.menu.modifierSets.find(
      ({ id }) => id === modifierSetId,
    );

    if (modifierSet) {
      modifierSets.push(modifierSet);
    }
  });

  return modifierSets;
});

const selectedModifiers = computed(() => {
  const selectedModifiers: ModifierResource[] = [];

  modifierSets.value.forEach((modifierSet) => {
    modifierSet.modifiers.forEach((modifier) => {
      if (selectedModifierIds[modifierSet.id]?.includes(modifier.id)) {
        selectedModifiers.push(modifier);
      }
    });
  });

  return selectedModifiers;
});

const shouldDisplayMedia = computed(() => {
  if (props.product.image) {
    return true;
  }

  if (props.product.video && isOnline.value) {
    return true;
  }

  return false;
});

const pendingAddToCartTotalPrice = computed(() => {
  let price = 0;

  const variant = props.product.variants.find(
    ({ id }) => id === selectedVariantId.value,
  );

  if (variant) {
    price += variant.price ?? 0;
  }

  selectedModifiers.value.forEach((modifier) => {
    if (modifier.price) {
      price += modifier.price;
    }
  });

  return price * quantity.value;
});

const orderingConfiguration = computed(() => {
  return restaurant.orderingConfiguration;
});

const selectedModifierIds = reactive(
  toValue(modifierSets).reduce(
    (acc, modifierSet) => {
      acc[modifierSet.id] = [];

      return acc;
    },
    {} as Record<number, number[]>,
  ),
);

const selectedVariantId = ref<number | null>(
  props.product.variants.length > 0 ? props.product.variants[0].id : null,
);

const quantity = ref(1);

useEventListener(window, 'keydown', (event) => {
  if (event.key === 'Escape') {
    hide();
  }
});

const onScroll = (event: Event) => {
  const target = event.target as HTMLElement;

  // Hide popup on overscroll.
  const overscrollTop = -target.scrollTop;
  const overscrollBottom = -(
    target.scrollHeight -
    target.offsetHeight -
    target.scrollTop
  );
  const overscrollThreshold = 130;
  if (
    overscrollTop > overscrollThreshold ||
    overscrollBottom > overscrollThreshold
  ) {
    hide();
    return;
  }
};

const onClickOutsideContent = () => {
  hide();
};

const hide = () => emit('hide');

const trackProductView = () => {
  axios
    .post('/api/product-views', {
      restaurantId: restaurant.id,
      productId: props.product.id,
    })
    .finally(() => {
      //
    });
};

onMounted(trackProductView);

const addToCart = () => {
  if (props.product.variants.length === 0) {
    throw new Error('There are no variants.'); // todo
  }

  if (!selectedVariantId.value) {
    throw new Error('No variant selected.');
  }

  if (!checkMinSelectionRequirements()) {
    return;
  }

  const modifierIds = selectedModifiers.value.map(({ id }) => id);

  const currentQuantity =
    cartStore.cartItemsIds.find(
      (cartItemIds) =>
        cartItemIds.productId === props.product.id &&
        cartItemIds.variantId === selectedVariantId.value &&
        isEqual(cartItemIds.modifierIds, modifierIds),
    )?.quantity ?? 0;

  cartStore.setCartItemQuantity({
    productId: props.product.id,
    variantId: selectedVariantId.value,
    modifierIds,
    quantity: currentQuantity + quantity.value,
  });

  hide();
};

const checkMinSelectionRequirements = () => {
  let isValid = true;

  modifierSets.value.forEach((modifierSet) => {
    if (
      modifierSet.minSelection !== null &&
      modifierSet.minSelection > selectedModifierIds[modifierSet.id].length
    ) {
      toast.error(
        `Min selection of ${modifierSet.minSelection} is required for "${localize(modifierSet.name)}"`,
      );
      isValid = false;
    }
  });

  return isValid;
};

const selectionRequirementsLabelForModifierSet = (
  modifierSet: ModifierSetResource,
) => {
  if (modifierSet.minSelection === 1 && modifierSet.maxSelection === 1) {
    return trans('Required');
  }

  const parts: string[] = [];

  if (modifierSet.minSelection !== null && modifierSet.minSelection >= 1) {
    parts.push(`Min: ${modifierSet.minSelection}`);
  } else {
    parts.push(trans('Optional'));
  }

  if (modifierSet.maxSelection !== null) {
    parts.push(`Max: ${modifierSet.maxSelection}`);
  }

  return parts.join(', ');
};

const selectModifier = (
  modifierSet: ModifierSetResource,
  modifier: ModifierResource,
) => {
  if (modifierSet.minSelection === 1 && modifierSet.maxSelection === 1) {
    selectedModifierIds[modifierSet.id] = [modifier.id];
  } else {
    const index = selectedModifierIds[modifierSet.id].indexOf(modifier.id);

    if (index !== -1) {
      selectedModifierIds[modifierSet.id].splice(index, 1);
    } else if (
      modifierSet.maxSelection !== null &&
      modifierSet.maxSelection <= selectedModifierIds[modifierSet.id].length
    ) {
      toast.error(
        `Max selection of ${modifierSet.maxSelection} is reached for "${localize(modifierSet.name)}"`,
      );
    } else {
      selectedModifierIds[modifierSet.id].push(modifier.id);
    }
  }
};
</script>
